import { Controller } from "@hotwired/stimulus";
import { trackProofEvent } from "../proof_events.js";
import { contactPayload } from "../contact_request.js";

const KEY = "0x4AAAAAAFYRzMXsNwOqmWd9";
let loading;
const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ||= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.onload = () => resolve(window.turnstile);
    script.onerror = reject;
    document.head.appendChild(script);
  }).catch((error) => {
    loading = null;
    throw error;
  });
  return loading;
};

export default class extends Controller {
  static targets = ["challenge", "status", "submit", "fields"];

  get english() {
    return document.documentElement.lang.startsWith("en");
  }
  say(fr, en) {
    return this.english ? en : fr;
  }

  async connect() {
    const types = {
      "ai-audit": "ai_audit",
      "ai-integration": "ai_integration",
      "ai-training": "ai_training",
      "custom-software-brittany": "software",
    };
    const project = this.element.elements.project_type;
    if (!project.value)
      project.value =
        types[new URLSearchParams(location.search).get("ref")] || "";
    this.token = "";
    this.submitTarget.disabled = true;
    this.verificationStarted = false;
    this.startVerification = () => this.verify();
    this.element.addEventListener("focusin", this.startVerification, { once: true });
    this.element.addEventListener("pointerdown", this.startVerification, { once: true, capture: true });
  }

  async verify() {
    if (this.verificationStarted) return;
    this.verificationStarted = true;
    this.element.removeEventListener("focusin", this.startVerification);
    this.element.removeEventListener("pointerdown", this.startVerification, true);
    try {
      const api = await loadTurnstile();
      if (!this.element.isConnected) return;
      this.widget = api.render(this.challengeTarget, {
        sitekey: KEY,
        callback: (token) => {
          this.token = token;
          this.submitTarget.disabled = this.sending || this.sent;
        },
        "expired-callback": () => {
          this.token = "";
          this.submitTarget.disabled = true;
          api.reset(this.widget);
        },
        "error-callback": () => {
          this.token = "";
          this.submitTarget.disabled = true;
          this.statusTarget.hidden = false;
          this.statusTarget.setAttribute("role", "alert");
          this.statusTarget.textContent = this.say(
            "La vérification anti-robot est indisponible. Rechargez la page pour réessayer.",
            "The anti-bot check is unavailable. Reload the page to try again.",
          );
        },
      });
    } catch {
      this.statusTarget.hidden = false;
      this.statusTarget.setAttribute("role", "alert");
      this.statusTarget.textContent = this.say(
        "La vérification anti-robot n’a pas chargé. Rechargez la page pour réessayer.",
        "The anti-bot check did not load. Reload the page to try again.",
      );
    }
  }

  disconnect() {
    this.element.removeEventListener("focusin", this.startVerification);
    this.element.removeEventListener("pointerdown", this.startVerification, true);
    if (this.widget !== undefined) window.turnstile?.remove(this.widget);
  }

  async submit(event) {
    event.preventDefault();
    if (this.sending || !this.token || !this.element.reportValidity()) return;
    this.sending = true;
    this.submitTarget.disabled = true;
    this.element.setAttribute("aria-busy", "true");
    this.statusTarget.hidden = false;
    this.statusTarget.setAttribute("role", "status");
    this.statusTarget.textContent = this.say("Envoi en cours…", "Sending…");
    const offer = { ai_audit: "audit", ai_integration: "integration", ai_training: "training", software: "custom-software" }[this.element.elements.project_type.value] || "other";
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 20000);
    try {
      const response = await fetch(this.element.action, {
        method: "POST",
        signal: abort.signal,
        credentials: "omit",
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body: JSON.stringify(
          contactPayload(new FormData(this.element), location.href, this.token),
        ),
      });
      const body = await response.json();
      if (!response.ok || body.status !== "received")
        throw new Error(body.error || "unavailable");
      this.statusTarget.textContent = this.say(
        "Votre demande est bien reçue. Je vous réponds sous 24 h ouvrées. Vous pouvez ajouter une précision en répondant à l’email de confirmation.",
        "Your enquiry has been received. I will reply within 24 business hours. You can add details by replying to the confirmation email.",
      );
      this.sent = true;
      this.element.reset();
      this.fieldsTarget.hidden = true;
      this.challengeTarget.hidden = true;
      trackProofEvent("contact_submit_success", offer);
      try { window.sa_event?.("contact_request_sent"); } catch { /* Analytics cannot invalidate an accepted request. */ }
    } catch (error) {
      this.statusTarget.setAttribute("role", "alert");
      this.statusTarget.textContent =
        error.message === "rate_limit"
          ? this.say(
              "Plusieurs demandes sont déjà parties de votre connexion. Réessayez dans quelques minutes.",
              "Several enquiries have already been sent from your connection. Please try again in a few minutes.",
            )
          : this.say(
              "Votre demande n’a pas pu être confirmée. Vos réponses sont conservées ; réessayez après la vérification anti-robot.",
              "Your enquiry could not be confirmed. Your answers have been kept; please try again after the anti-bot check.",
            );
      this.token = "";
      window.turnstile?.reset(this.widget);
    } finally {
      clearTimeout(timeout);
      this.statusTarget.focus();
      this.sending = false;
      this.element.removeAttribute("aria-busy");
      if (!this.sent) this.submitTarget.disabled = !this.token;
    }
  }
}
